/**
 * Pure, testable client-side field splitting for tenant connector forms.
 *
 * Client-side half of secret isolation: a write_only (secret) field is NEVER placed in the non-secret
 * config PATCH body, and a non-secret config field is NEVER placed in the write-only /secrets body.
 * Blank inputs are omitted (a blank secret keeps the current value; the value is never round-tripped).
 * The backend re-enforces this (400 SECRET_FIELD_IN_CONFIG / UNKNOWN_SECRET_FIELD); this keeps the UI
 * from ever sending a secret to the wrong place.
 */
export function buildConfigBody(schema, fields) {
  const out = {};
  (schema || []).filter((f) => !f.write_only).forEach((f) => {
    const v = fields ? fields[f.field] : undefined;
    if (v !== '' && v != null) out[f.field] = v;
  });
  return out;
}

export function buildSecretsBody(schema, fields) {
  const secrets = {};
  (schema || []).filter((f) => f.write_only).forEach((f) => {
    const v = fields ? fields[f.field] : undefined;
    if (v) secrets[f.field] = v;   // blank omitted → keeps existing value
  });
  return secrets;
}

/** Fields the form should render for a given editor kind (secrets vs config). */
export function fieldsForKind(schema, kind) {
  return (schema || []).filter((f) => (kind === 'secrets' ? f.write_only : !f.write_only));
}

/**
 * Client-side validation for a downgrade retention selection (P16 #14). Mirrors the server guard so
 * the UI never submits an invalid selection; the backend re-enforces (403/409/400). retainCap is the
 * effective allowance (a number, or Infinity for Unlimited).
 */
export function retentionSelectionValid(selectedIds, retainCap) {
  const ids = selectedIds || [];
  if (ids.length === 0) return { ok: false, reason: 'EMPTY' };
  if (ids.length !== new Set(ids).size) return { ok: false, reason: 'DUPLICATE' };
  if (retainCap !== Infinity && ids.length > retainCap) return { ok: false, reason: 'EXCEEDS_ALLOWANCE' };
  return { ok: true };
}
